import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle, XCircle, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRLeaveApprovals = () => {
  const { getAuthHeader } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningLeave, setActioningLeave] = useState(null);
  const [comments, setComments] = useState('');

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/hr/leave-requests`, {
        headers: getAuthHeader()
      });
      setLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/employee/hr/leave-requests/${leaveId}/approve`,
        null,
        {
          headers: getAuthHeader(),
          params: { comments }
        }
      );
      alert('Leave approved successfully!');
      setActioningLeave(null);
      setComments('');
      fetchLeaves();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    if (!comments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    try {
      await axios.put(
        `${BACKEND_URL}/api/employee/hr/leave-requests/${leaveId}/reject`,
        null,
        {
          headers: getAuthHeader(),
          params: { comments }
        }
      );
      alert('Leave rejected');
      setActioningLeave(null);
      setComments('');
      fetchLeaves();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject leave');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const processedLeaves = leaves.filter(l => l.status !== 'pending');

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Leave Approvals</h2>

      {/* Pending Approvals */}
      <Card>
        <CardHeader>
          <CardTitle>Pending Approvals ({pendingLeaves.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {pendingLeaves.length > 0 ? (
            <div className="space-y-4">
              {pendingLeaves.map((leave) => (
                <div key={leave.id} className="border rounded-lg p-4 bg-yellow-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="text-gray-600" size={16} />
                        <span className="font-bold">{leave.employee_name}</span>
                        <span className="text-sm text-gray-600">({leave.employee_id})</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">{leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave</span>
                        <Badge className="bg-gray-200 text-gray-800">{leave.days} days</Badge>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                        <Calendar size={14} />
                        {new Date(leave.from_date).toLocaleDateString()} - {new Date(leave.to_date).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-700">
                        <strong>Reason:</strong> {leave.reason}
                      </div>
                    </div>
                  </div>

                  {actioningLeave === leave.id ? (
                    <div className="mt-3 space-y-3 border-t pt-3">
                      <Textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Comments (optional for approval, required for rejection)"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(leave.id)}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(leave.id)}
                          className="bg-red-600 hover:bg-red-700"
                          size="sm"
                        >
                          <XCircle size={16} className="mr-1" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => {
                            setActioningLeave(null);
                            setComments('');
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t">
                      <Button
                        onClick={() => setActioningLeave(leave.id)}
                        className="bg-primary hover:bg-primary/90"
                        size="sm"
                      >
                        Review
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No pending leave requests</p>
          )}
        </CardContent>
      </Card>

      {/* Processed Leaves */}
      <Card>
        <CardHeader>
          <CardTitle>Recently Processed</CardTitle>
        </CardHeader>
        <CardContent>
          {processedLeaves.length > 0 ? (
            <div className="space-y-3">
              {processedLeaves.slice(0, 10).map((leave) => (
                <div key={leave.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{leave.employee_name}</span>
                        <Badge className={getStatusBadge(leave.status)}>
                          {leave.status.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        {leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} • {new Date(leave.from_date).toLocaleDateString()} - {new Date(leave.to_date).toLocaleDateString()} • {leave.days} days
                      </div>
                      {leave.review_comments && (
                        <div className="text-sm text-gray-700 mt-2">
                          <strong>Comment:</strong> {leave.review_comments}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Reviewed by: {leave.reviewed_by}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No processed leaves yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HRLeaveApprovals;
